import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { CalendarAnnouncementSelectList } from './CalendarAnnouncementSelectList';
import { Form, FormError, FormField, FormProps, FormSchemaEntry, HeroIconName, LoadingSpinner } from '@gik/ui';
import { useCalendarAnnouncementTypes } from '@gik/api/calendar/calendarAnnouncementType';
import CalendarAnnouncement from './CalendarAnnouncement';
import { FormRenderProps } from 'react-final-form';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { timeFromInt } from 'time-number';
import { ICalendarEventTimeAndRepeatFormValues } from '../EventForm/CalendarEventForm';
import noop from '@gik/core/utils/noop';
import { FormApi, Mutator } from 'final-form';
import { calendarTimeAndRepeatSchema } from '../EventForm/EventFormSchema';
import { CalendarTimeAndRepeatForm } from '../EventForm/CalendarTimeAndRepeatForm';
import { translationKeys } from './i18n/en';
import i18n from '@gik/i18n';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { dateFormat } from '@gik/l10n';
import { applyTimeToMoment } from '@gik/core/utils/moment';
import { dateTimeFormat } from '@gik/core/utils/DateTimeUtils';

export enum CalendarAnnouncementFormFieldNames {
  Title = 'title',
  Description = 'description',
  AllDay = 'allDay',
  StartDate = 'startDate',
  StartTime = 'startTime',
  EndDate = 'endDate',
  EndTime = 'endTime',
  Repeat = 'repeat',
  Weekdays = 'weekday',
  AnnouncementTypeId = 'announcementTypeId',
  SendEmailToPageFollowers = 'sendEmailToPageFollowers',
}

export interface ICalendarAnnouncementFormValues extends ICalendarEventTimeAndRepeatFormValues {
  [CalendarAnnouncementFormFieldNames.Title]: string;
  [CalendarAnnouncementFormFieldNames.Description]: string;
  [CalendarAnnouncementFormFieldNames.AnnouncementTypeId]: number;
  [CalendarAnnouncementFormFieldNames.SendEmailToPageFollowers]?: boolean;
}

export const calendarAnnouncementFormSchema = (formValues: ICalendarAnnouncementFormValues): FormSchemaEntry[] => {
  const schema = [
    {
      type: 'text',
      name: CalendarAnnouncementFormFieldNames.Title,
      required: true,
      label: i18n.t(translationKeys.formTitleLabel),
      placeholder: i18n.t(translationKeys.formTitlePlaceholder),
      maxLength: 255,
      props: {
        maxLength: 255,
        maxLengthDisplay: true,
        maxLengthCentered: true,
      },
    },
    {
      type: 'textarea',
      name: CalendarAnnouncementFormFieldNames.Description,
      required: true,
      label: i18n.t(translationKeys.formDescriptionLabel),
      placeholder: i18n.t(translationKeys.formDescriptionPlaceholder),
      maxLength: 1000,
      props: {
        rows: 6,
        maxLength: 1000,
        maxLengthDisplay: true,
      },
    },
    {
      name: CalendarAnnouncementFormFieldNames.SendEmailToPageFollowers,
      type: 'checkbox',
      props: {
        label: i18n.t(translationKeys.formEmailToPageFollowersLabel),
      },
    },
    ...calendarTimeAndRepeatSchema(formValues, true),
  ] as FormSchemaEntry[];

  return schema;
};

export interface ICalendarAnnouncementFormProps extends FormProps {
  initialValues?: Partial<ICalendarAnnouncementFormValues>;
  errorMessage?: string;
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  onChange?: (values: ICalendarAnnouncementFormValues) => void;
  onSubmit?: (values: ICalendarAnnouncementFormValues) => void;
}

const emptyValues: ICalendarAnnouncementFormValues = {
  title: null,
  description: null,
  repeat: null,
  startDate: undefined,
  endDate: undefined,
  startTime: undefined,
  endTime: undefined,
  allDay: false,
  weekdays: null,
  announcementTypeId: null,
};

export function CalendarAnnouncementForm({
  errorMessage,
  className,
  buttons,
  buttonsPortal,
  initialValues,
  onSubmit = noop,
  onChange = noop,
  ...otherProps
}: ICalendarAnnouncementFormProps): React.ReactElement {
  const bem = bemBlock('calendar-announcement-form');
  const { t } = useTranslation();

  const formId = 'CalendarAnnouncementForm';
  let formApi: FormApi;

  const [_initialValues, setInitialValues] = React.useState<ICalendarAnnouncementFormValues>();
  const [rerender, setRerender] = React.useState<boolean>(false);
  const formApiRef = React.useRef<FormApi>();

  const valuesRef = React.useRef<ICalendarAnnouncementFormValues>();

  const [announcementTypeId, setAnnouncementTypeId] = React.useState<number>(initialValues.announcementTypeId);

  const { data: announcementTypes } = useCalendarAnnouncementTypes();

  /**
   * Scroll error into view if there is one
   */
  React.useEffect(() => {
    if (errorMessage) {
      // if there is an error we need to wait one render cycle for the dom element to become available
      setTimeout(() => {
        const el = document.querySelector(`#${formId} .gik-form-error`);
        if (el) {
          el.scrollIntoView();
        }
      });
    }
  }, [errorMessage]);

  /**
   * Populate initial form values
   *
   * note: this potentialy needs to wait to get products by event type id if no services were provided
   */
  React.useEffect(() => {
    if (_initialValues) {
      return;
    }

    const values = {
      ...emptyValues,
      // startTime: timeToInt('12:00'),
      ...initialValues,
    };

    valuesRef.current = values;
    setInitialValues(values);
  }, [initialValues, _initialValues]);

  function handleTypeChange(id: number) {
    setAnnouncementTypeId(id);
  }

  function handleChange(values: ICalendarAnnouncementFormValues) {
    // handle startTime change
    if (
      values.startTime !== undefined &&
      values.startTime !== null &&
      values.startTime !== valuesRef.current?.startTime
    ) {
      setTimeout(() => {
        // set endTime to be one hour after the startTime
        formApi.mutators.updateEndTime(values.startTime + 3600);
      });
    }

    // if startDate changes the endDate should be set to one month after the startDate (only if there is to initial endDate set when we are editing an existing entry)
    if (!valuesRef.current?.endDate || values.startDate !== valuesRef.current?.startDate) {
      formApi.mutators.updateEndDate(moment(values.startDate).add(1, 'month').format(dateFormat));
    }

    // if repeat changes rerender the form
    if (values.repeat !== valuesRef.current?.repeat) {
      formRerender();
    }

    valuesRef.current = values;
    setInitialValues(values);
    onChange(values);
  }

  async function handleSubmit(values: ICalendarAnnouncementFormValues) {
    values.announcementTypeId = announcementTypeId;
    onSubmit(values);
  }

  function formRerender() {
    setRerender(true);
    setTimeout(() => {
      setRerender(false);
    }, 1);
  }

  const updateStartTime: Mutator = (args, state, utils) => {
    utils.changeValue(state, 'startTime', () => args[0]);
  };
  const updateEndTime: Mutator = (args, state, utils) => {
    utils.changeValue(state, 'endTime', () => args[0]);
  };
  const updateEndDate: Mutator = (args, state, utils) => {
    utils.changeValue(state, 'endDate', () => args[0]);
  };

  const isLoading = !announcementTypes || !_initialValues;
  if (isLoading) {
    return <LoadingSpinner center />;
  }

  const schema = calendarAnnouncementFormSchema(valuesRef.current);

  const announcementType = announcementTypes.find(item => item.id === announcementTypeId);

  let startDate = moment(valuesRef.current?.startDate);
  let endDate = moment(valuesRef.current?.endDate || valuesRef.current?.startDate);

  if (valuesRef.current && !valuesRef.current.allDay) {
    startDate = applyTimeToMoment(startDate.clone(), `${timeFromInt(valuesRef.current.startTime)}:00.000`);
    endDate = applyTimeToMoment(
      endDate.isValid ? endDate.clone() : startDate.clone().add(1, 'month'),
      `${timeFromInt(valuesRef.current.endTime)}:00.000`
    );
  }

  if (rerender) {
    return null;
  }

  return (
    <div className={bem(null, null, className)} {...otherProps}>
      <CalendarAnnouncementSelectList value={announcementTypeId} onChange={handleTypeChange} />
      {announcementType !== undefined && (
        <>
          <label>Preview</label>
          <CalendarAnnouncement
            type={announcementType?.slug}
            icon={announcementType?.acf.icon as HeroIconName}
            startsAt={startDate.format(dateTimeFormat)}
            endsAt={endDate.format(dateTimeFormat)}
            allDay={valuesRef.current?.allDay}
            title={valuesRef.current?.title || t(translationKeys.formTitlePlaceholder)}
            description={valuesRef.current?.description || t(translationKeys.formDescriptionPlaceholder)}
            customMoreButtonClick={null}
            displayTime={valuesRef.current.repeat !== undefined && valuesRef.current.repeat !== null}
          />

          <Form
            className={bem('form', null, className)}
            schema={schema}
            vertical
            id={formId}
            mutators={{ updateStartTime, updateEndTime, updateEndDate }}
            render={form => {
              formApi = form.form;
              formApiRef.current = form.form;
              console.log('Form isSubmitting: ', form.submitting);
              const _buttons = buttons ? buttons(form, formId) : null;

              return (
                <>
                  <FormField name={CalendarAnnouncementFormFieldNames.Title} />
                  <FormField name={CalendarAnnouncementFormFieldNames.Description} />

                  <CalendarTimeAndRepeatForm formValues={valuesRef.current} />

                  <FormField name={CalendarAnnouncementFormFieldNames.SendEmailToPageFollowers} />

                  <FormError centered message={errorMessage} />

                  {renderPortal(_buttons, buttonsPortal)}
                </>
              );
            }}
            {...otherProps}
            onSubmit={handleSubmit}
            onChange={handleChange}
            initialValues={{ ..._initialValues }}
          />
        </>
      )}
    </div>
  );
}
